import { AssetSearch } from "~/v2/components/AssetSearch/AssetSearch.connector";
import { CryptoOrderTicketConnector } from "./crypto/CryptoOrderTicket.connector";
import { EquityOrderTicketConnector } from "./equity/EquityOrderTicket.connector";
import { useOrderTicketController } from "./OrderTicket.controller";
import { OptionOrderTicketConnector } from "./option/OptionOrderTicket.connector";

export interface OrderTicketProps {
  symbol?: string;
  price?: number;
  quoteSide?: string;
  onCancel?: () => void;
  /** Fires when AssetSearch chooses a result — not while the query is typed. */
  onSymbolSelect?: (symbol: string) => void;
  /** Stretch to the parent instead of the asset-page card width. */
  fill?: boolean;
  /** One-shot quantity prefill from a workspace ticket draft. */
  initialQuantity?: number;
}

export const OrderTicketConnector = ({
  symbol: initialSymbol,
  price,
  quoteSide: initialQuoteSide,
  onCancel,
  onSymbolSelect,
  fill,
  initialQuantity,
}: OrderTicketProps) => {
  const {
    clock,
    product,
    account,
    symbol,
    isContractSymbol,
    isCryptoSymbol,
    margin,
    positions,
    quote,
    asset,
    contract,
    refetchQuote,
    onSymbolChange,
    isSymbolInputAutoFocus,
    feedLabel,
  } = useOrderTicketController({
    initialSymbol,
  });

  const onSearchSelect = (selected: string) => {
    onSymbolChange(selected);
    onSymbolSelect?.(selected);
  };

  // Option order ticket
  if (isContractSymbol) {
    return (
      <OptionOrderTicketConnector
        clock={clock}
        product={product}
        account={account}
        margin={margin}
        symbol={symbol}
        contract={contract}
        quote={quote}
        refetchQuote={refetchQuote}
        price={price}
        quoteSide={initialQuoteSide}
        onCancel={onCancel}
        positions={positions}
        fill={fill}
        initialQuantity={initialQuantity}
        AssetSearch={
          <AssetSearch
            placeholder="Search symbol or contract"
            value={symbol}
            onChange={onSymbolChange}
            onSelect={onSearchSelect}
            autofocus={isSymbolInputAutoFocus}
            showFilters={false}
          />
        }
      />
    );
  }

  // Crypto order ticket
  if (isCryptoSymbol) {
    return (
      <CryptoOrderTicketConnector
        product={product}
        account={account}
        margin={margin}
        positions={positions}
        symbol={symbol}
        asset={asset}
        quote={quote}
        refetchQuote={refetchQuote}
        fill={fill}
        initialQuantity={initialQuantity}
        AssetSearch={
          <AssetSearch
            value={symbol}
            onChange={onSymbolChange}
            onSelect={onSearchSelect}
            autofocus={isSymbolInputAutoFocus}
            showFilters={false}
          />
        }
      />
    );
  }

  // "us_equity" is the default orderTicket
  return (
    <EquityOrderTicketConnector
      clock={clock}
      product={product}
      account={account}
      symbol={symbol}
      margin={margin}
      positions={positions}
      quote={quote}
      refetchQuote={refetchQuote}
      asset={asset}
      feedLabel={feedLabel}
      fill={fill}
      initialQuantity={initialQuantity}
      AssetSearch={
        <AssetSearch
          value={symbol}
          onChange={onSymbolChange}
          onSelect={onSearchSelect}
          autofocus={isSymbolInputAutoFocus}
          showFilters={false}
        />
      }
    />
  );
};
